/**
 * @file Dashboard.js
 * @description Dashboard overview page component displaying transaction metrics, daily summary stats, and recent check cashing activity.
 */

import '../css/pages/Dashboard.css';
import React, { useState, useEffect, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import Layout from "../components/Layout";
import { getDashboardStats, getCheques } from "../api/services";
import { Link, useNavigate } from "react-router-dom";
import { formatDate, capitalizeName, getChicagoHour } from "../utils/formatDate";

/**
 * Formats amount into US currency string representation.
 * @param {number|string} n - Amount.
 * @returns {string} Formatted currency.
 */
const fmt = (n) =>
  "$" +
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const statusColor = {
  Cashed:   "badge-green",
  Pending:  "badge-yellow",
  Returned: "badge-red",
  Void:     "badge-gray",
  Hold:     "badge-blue",
};

/* ── Column definitions ─────────────────────────────────── */
const buildColumns = (navigate) => [
  {
    name: "FIRST NAME",
    selector: (row) => row.customer?.firstName,
    sortable: true,
    sortField: "customer",
    minWidth: "130px",
    cell: (row) =>
      row.customer?._id ? (
        <span
          className="table-customer-link"
          onClick={(e) => { e.stopPropagation(); navigate(`/customers/${row.customer._id}`); }}
        >
          {capitalizeName(row.customer.firstName)}
        </span>
      ) : (
        <strong>{capitalizeName(row.customer?.firstName)}</strong>
      ),
  },
  {
    name: "LAST NAME",
    selector: (row) => row.customer?.lastName,
    sortable: true,
    sortField: "customerLastName",
    minWidth: "130px",
    cell: (row) =>
      row.customer?._id ? (
        <span
          className="table-customer-link"
          onClick={(e) => { e.stopPropagation(); navigate(`/customers/${row.customer._id}`); }}
        >
          {capitalizeName(row.customer.lastName)}
        </span>
      ) : (
        <strong>{capitalizeName(row.customer?.lastName)}</strong>
      ),
  },
  {
    name: "CHECK #",
    selector: (row) => row.checkNumber,
    sortable: true,
    sortField: "checkNumber",
    minWidth: "130px",
    cell: (row) => <span className="td-mono">{row.checkNumber}</span>,
  },
  {
    name: "AMOUNT",
    selector: (row) => row.amount,
    sortable: true,
    sortField: "amount",
    right: true,
    minWidth: "110px",
    cell: (row) => <span className="amount">{fmt(row.amount)}</span>,
  },
  {
    name: "FEE",
    selector: (row) => row.fee,
    sortable: true,
    sortField: "fee",
    right: true,
    minWidth: "90px",
    cell: (row) => <span className="td-fee">{fmt(row.fee)}</span>,
  },
  {
    name: "PAY OUT",
    selector: (row) => row.payOut,
    sortable: true,
    sortField: "payOut",
    right: true,
    minWidth: "110px",
    cell: (row) => <span className="amount-positive">{fmt(row.payOut)}</span>,
  },
  {
    name: "STATUS",
    selector: (row) => row.status,
    sortable: true,
    sortField: "status",
    minWidth: "110px",
    cell: (row) => (
      <span className={`badge ${statusColor[row.status] || "badge-gray"}`}>
        {row.status}
      </span>
    ),
  },
  {
    name: "CASHED DATE",
    selector: (row) => row.dateCashed,
    sortable: true,
    sortField: "dateCashed",
    minWidth: "130px",
    cell: (row) => (
      <span className="td-date">
        {row.dateCashed ? formatDate(row.dateCashed) : "—"}
      </span>
    ),
  },
  {
    name: "CHECK DATE",
    selector: (row) => row.checkDate,
    sortable: true,
    sortField: "checkDate",
    minWidth: "130px",
    cell: (row) => (
      <span className="td-date td-date-muted">
        {row.checkDate ? formatDate(row.checkDate) : "—"}
      </span>
    ),
  },
];

/* ── Custom DataTable styles matching app theme ─────────── */
const dtStyles = {
  headRow: {
    style: {
      backgroundColor: "#f8fafc",
      borderBottom: "1px solid var(--gray-3)",
      minHeight: "44px",
    },
  },
  headCells: {
    style: {
      fontSize: "11.5px",
      fontWeight: "800",
      color: "var(--gray-1)",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  rows: {
    style: {
      fontSize: "13.5px",
      color: "var(--dark)",
      borderBottom: "1px solid var(--gray-4)",
      minHeight: "54px",
      cursor: "pointer",
      transition: "background 0.15s ease",
      "&:hover": {
        backgroundColor: "rgba(26, 122, 74, 0.04)",
      },
    },
  },
  cells: {
    style: {
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  pagination: {
    style: {
      borderTop: "1px solid var(--gray-3)",
      fontSize: "13px",
      color: "var(--gray-1)",
      minHeight: "52px",
    },
    pageButtonsStyle: {
      borderRadius: "6px",
      color: "var(--dark)",
      fill: "var(--gray-1)",
      "&:hover:not(:disabled)": {
        backgroundColor: "var(--gray-4)",
      },
      "&:focus": {
        outline: "none",
        backgroundColor: "var(--gray-4)",
      },
    },
  },
  noData: {
    style: {
      padding: "40px",
      color: "var(--gray-1)",
      fontSize: "13.5px",
    },
  },
  progress: {
    style: {
      padding: "40px",
    },
  },
};

/* ── Conditional row styling by status ─────────────────── */
const conditionalRowStyles = [
  {
    when: (row) => row.status === "Returned",
    style: { backgroundColor: "rgba(239, 68, 68, 0.04)" },
  },
  {
    when: (row) => row.status === "Hold",
    style: { backgroundColor: "rgba(59, 130, 246, 0.04)" },
  },
  {
    when: (row) => row.status === "Pending",
    style: { backgroundColor: "rgba(245, 158, 11, 0.04)" },
  },
];

/**
 * Main Dashboard page component.
 * @component
 * @returns {React.ReactElement}
 */
export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [txSearch, setTxSearch]   = useState("");
  const [txSearchInput, setTxSearchInput] = useState("");
  const [txSearchType, setTxSearchType]   = useState("name");

  const [cheques, setCheques]     = useState([]);
  const [txTotal, setTxTotal]     = useState(0);
  const [txLoading, setTxLoading] = useState(false);
  const [txPage, setTxPage]       = useState(1);
  const [txLimit, setTxLimit]     = useState(10);
  const [txSort, setTxSort]       = useState({ column: {}, direction: "desc" });

  const columns = buildColumns(navigate);

  const statsLoadedRef = useRef(false);

  useEffect(() => {
    if (statsLoadedRef.current) return;
    statsLoadedRef.current = true;
    getDashboardStats()
      .then((res) => setStats(res.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const lastChequeTimeRef   = useRef(0);
  const lastChequeParamsRef = useRef("");

  const fetchCheques = useCallback((page, perPage, search, searchType, sort) => {
    const sortField = sort?.column?.sortField || "dateCashed";
    const sortDir   = sort?.direction === "asc" ? sortField : `-${sortField}`;
    const params    = { page, limit: perPage, sort: sortDir };
    if (search.trim()) {
      if (searchType === "name")  params.customerName  = search.trim();
      else if (searchType === "ssn")   params.socialNo      = search.trim();
      else if (searchType === "phone") params.customerPhone = search.trim();
    }

    // Deduplicate identical requests within 400ms (React StrictMode double-mount)
    const paramsKey = JSON.stringify(params);
    const now = Date.now();
    if (paramsKey === lastChequeParamsRef.current && now - lastChequeTimeRef.current < 400) {
      return;
    }
    lastChequeParamsRef.current = paramsKey;
    lastChequeTimeRef.current = now;

    setTxLoading(true);
    getCheques(params)
      .then((res) => {
        setCheques(res.data.data || []);
        setTxTotal(res.data.total || 0);
      })
      .catch(console.error)
      .finally(() => setTxLoading(false));
  }, []);

  useEffect(() => {
    fetchCheques(txPage, txLimit, txSearch, txSearchType, txSort);
  }, [txPage, txLimit, txSearch, txSearchType, txSort, fetchCheques]);

  // Search only fires on button click or Enter key — not on every keystroke.

  const handleSearch = () => {
    setTxSearch(txSearchInput);
    setTxPage(1);
  };
  const handleClear  = () => { setTxSearchInput(""); setTxSearch(""); setTxPage(1); };

  const handleTypeChange = (e) => {
    setTxSearchType(e.target.value);
    setTxSearchInput("");
    setTxSearch("");
    setTxPage(1);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    if (txSearchType === "ssn") {
      const d = val.replace(/\D/g, "").slice(0, 9);
      let f = d;
      if (d.length > 5) f = d.slice(0,3) + "-" + d.slice(3,5) + "-" + d.slice(5);
      else if (d.length > 3) f = d.slice(0,3) + "-" + d.slice(3);
      setTxSearchInput(f);
    } else if (txSearchType === "phone") {
      const d = val.replace(/\D/g, "").slice(0, 10);
      let f = d;
      if (d.length > 6)      f = "(" + d.slice(0,3) + ") " + d.slice(3,6) + "-" + d.slice(6);
      else if (d.length > 3) f = "(" + d.slice(0,3) + ") " + d.slice(3);
      else if (d.length > 0) f = "(" + d;
      setTxSearchInput(f);
    } else {
      setTxSearchInput(val);
    }
  };

  const handleSort = (column, direction) => {
    setTxPage(1);
    setTxSort({ column, direction });
  };

  const handlePageChange = (page) => setTxPage(page);

  const handlePerRowsChange = (newPerPage, page) => {
    setTxLimit(newPerPage);
    setTxPage(page);
  };

  const handleRowClick = (row) => {
    if (row.customer?._id) navigate(`/customers/${row.customer._id}`);
  };

  if (loading)
    return (
      <Layout title="Dashboard">
        <div className="loading-screen">
          <div className="spinner spinner-dark" />
        </div>
      </Layout>
    );

  const {
    today = {}, month = {}, year = {},
    totalCustomers = 0, newCustomersToday = 0,
    blockedCustomers = 0, blockedCompanies = 0,
    statusBreakdown = [],
  } = stats || {};

  const totalStatusCount = statusBreakdown.reduce((sum, item) => sum + (item.count || 0), 0) || 1;

  const todayHour = getChicagoHour();
  const greetingText = todayHour < 12 ? "Good Morning" : todayHour < 17 ? "Good Afternoon" : "Good Evening";

  const formattedDate = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const appEnv = process.env.REACT_APP_ENV || 'live';
  const envLabel = appEnv.charAt(0).toUpperCase() + appEnv.slice(1);

  return (
    <Layout title="Dashboard">
      {/* Modern Hero Welcome bar */}
      <div className="dashboard-welcome">
        <div className="dashboard-greeting-group">
          <div className="dashboard-greeting-badge">
            <span className="status-dot-pulse" /> {envLabel} Store Dashboard • {formattedDate}
          </div>
          <h1 className="dashboard-greeting">
            {greetingText}, Admin 👋
          </h1>
          <p className="dashboard-greeting-sub">
            Real-time check cashing metrics & financial insights for your store.
          </p>
        </div>
        <div className="dashboard-welcome-actions">
          <Link to="/cheques/new" className="btn-hero-primary">
            💵 Cash a Check
          </Link>
          <Link to="/customers/new" className="btn-hero-secondary">
            ➕ Add Customer
          </Link>
        </div>
      </div>

      {/* Modern Stat cards Grid */}
      <div className="stat-grid">
        {/* Combined Today's Performance Box (1 Standard Box Size) */}
        <div className="stat-card stat-card-combined stat-card-today">
          <div className="stat-combined-group">
            <div className="stat-combined-item">
              <div className="stat-label">Today Vol</div>
              <div className="stat-value-sm">{fmt(today.total)}</div>
              <div className="stat-sub">{today.count || 0} checks</div>
            </div>
            <div className="stat-combined-divider" />
            <div className="stat-combined-item">
              <div className="stat-label">Revenue</div>
              <div className="stat-value-sm stat-value-accent">{fmt(today.fee)}</div>
              <div className="stat-sub">Fees earned</div>
            </div>
          </div>
        </div>

        <div className="stat-card stat-card-monthly">
          <div className="stat-info">
            <div className="stat-label">Monthly Volume</div>
            <div className="stat-value val-monthly">{fmt(month.total)}</div>
            <div className="stat-sub">{month.count || 0} checks processed</div>
          </div>
        </div>

        <div className="stat-card stat-card-customers">
          <div className="stat-info">
            <div className="stat-label">Total Customers</div>
            <div className="stat-value val-customers">{totalCustomers.toLocaleString("en-US")}</div>
            <div className="stat-sub">+{newCustomersToday} added today</div>
          </div>
        </div>

        <Link to="/blocked-customers" className="stat-card-link">
          <div className="stat-card stat-card-clickable stat-card-blocked-cust">
            <div className="stat-info">
              <div className="stat-label stat-label-danger">Blocked Customers</div>
              <div className="stat-value val-danger">
                {blockedCustomers.toLocaleString("en-US")}
              </div>
              <div className="stat-sub">Click to review alerts</div>
            </div>
          </div>
        </Link>

        <Link to="/blocked-companies" className="stat-card-link">
          <div className="stat-card stat-card-clickable stat-card-blocked-comp">
            <div className="stat-info">
              <div className="stat-label stat-label-danger">Blocked Companies</div>
              <div className="stat-value val-warning">
                {blockedCompanies.toLocaleString("en-US")}
              </div>
              <div className="stat-sub">Click to review alerts</div>
            </div>
          </div>
        </Link>
      </div>

      {/* Bottom Main Content Grid */}
      <div className="dashboard-bottom-grid">
        {/* Recent Transactions — DataTable */}
        <div className="card">
          <div className="card-header-styled">
            <h3>
              <span>⚡ Recent Transactions</span>
              <span className="card-header-count">{txTotal} Total</span>
            </h3>
            <Link to="/cheques" className="btn btn-ghost btn-sm">View all →</Link>
          </div>

          {/* Search bar */}
          <div className="tx-search-bar">
            <div className="tx-search-inner">
              <select value={txSearchType} onChange={handleTypeChange} className="tx-search-type">
                <option value="name">👤 Name</option>
                <option value="ssn">🔒 SSN</option>
                <option value="phone">📞 Phone</option>
              </select>
              <div className="tx-search-input-wrap">
                <input
                  type="text"
                  value={txSearchInput}
                  onChange={handleInputChange}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder={
                    txSearchType === "name"  ? "Search customer name..." :
                    txSearchType === "ssn"   ? "Search SSN (e.g. xxx-xx-xxxx)..." :
                                               "Search phone (e.g. (xxx) xxx-xxxx)..."
                  }
                  className="tx-search-input"
                />
                {txSearchInput && (
                  <button onClick={handleClear} className="tx-search-clear" title="Clear">✕</button>
                )}
              </div>
              <button onClick={handleSearch} className="btn btn-primary tx-search-btn">
                🔍 Search
              </button>
            </div>
          </div>

          {/* DataTable */}
          <DataTable
            columns={columns}
            data={cheques}
            progressPending={txLoading}
            progressComponent={
              <div className="table-loading-cell">
                <div className="spinner spinner-dark spinner-centered" />
              </div>
            }
            noDataComponent={
              <div className="table-empty-cell">
                {txSearch ? `No transactions match "${txSearch}"` : "No transactions yet"}
              </div>
            }
            pagination
            paginationServer
            paginationTotalRows={txTotal}
            paginationDefaultPage={txPage}
            paginationPerPage={txLimit}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            sortServer
            onSort={handleSort}
            defaultSortFieldId={7}
            defaultSortAsc={false}
            onRowClicked={handleRowClick}
            highlightOnHover
            pointerOnHover
            customStyles={dtStyles}
            conditionalRowStyles={conditionalRowStyles}
            dense={false}
          />
        </div>

        {/* Right column Analytics & Speed Dial */}
        <div className="dashboard-right-col">
          {/* Year to Date */}
          <div className="card">
            <div className="card-header-styled">
              <h3>📈 YTD Performance</h3>
            </div>
            <div className="card-body">
              {[
                { label: "Total Volume",       val: fmt(year.total),                          colorClass: "ytd-val-primary" },
                { label: "Fee Revenue",         val: fmt(year.fee),                            colorClass: "ytd-val-accent"  },
                { label: "Total Transactions",  val: (year.count || 0).toLocaleString("en-US"), colorClass: "ytd-val-info"   },
              ].map((r) => (
                <div key={r.label} className="ytd-row">
                  <span className="ytd-label">{r.label}</span>
                  <span className={`ytd-val ${r.colorClass}`}>{r.val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Status Breakdown & Distribution Bar */}
          <div className="card">
            <div className="card-header-styled">
              <h3>📊 Status Breakdown</h3>
            </div>
            <div className="card-body">
              {statusBreakdown.length === 0 ? (
                <p className="text-muted text-sm">No data yet</p>
              ) : (
                <>
                  <div className="status-dist-bar-wrap">
                    <div className="status-dist-bar">
                      {statusBreakdown.map((s) => {
                        const pct = ((s.count || 0) / totalStatusCount) * 100;
                        const segClass =
                          s._id === "Cashed" ? "seg-cashed" :
                          s._id === "Pending" ? "seg-pending" :
                          s._id === "Returned" ? "seg-returned" :
                          s._id === "Hold" ? "seg-hold" : "seg-void";
                        return (
                          <div
                            key={s._id}
                            className={`status-dist-segment ${segClass}`}
                            style={{ width: `${pct}%` }}
                            title={`${s._id}: ${s.count} checks (${pct.toFixed(1)}%)`}
                          />
                        );
                      })}
                    </div>
                  </div>

                  {statusBreakdown.map((s) => (
                    <div key={s._id} className="status-breakdown-row">
                      <span className={`badge ${statusColor[s._id] || "badge-gray"}`}>{s._id}</span>
                      <div className="text-right">
                        <div className="status-breakdown-count">{s.count} checks</div>
                        <div className="status-breakdown-total">{fmt(s.total)}</div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header-styled">
              <h3>⚡ Speed Dial</h3>
            </div>
            <div className="quick-actions-body">
              <Link to="/cheques/new" className="quick-action-btn primary">
                💵 Cash a Check
              </Link>
              <Link to="/customers/new" className="quick-action-btn outline">
                ➕ Add Customer
              </Link>
              <Link to="/cheques?status=Pending" className="quick-action-btn ghost">
                ⏳ View Pending Checks
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
